import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { createChatSession } from '../../../services/chatService';
import * as agentService from '../../../services/agentService';
import { Agent } from '../../../types/agent';
import { ChatInput } from '../ui/ChatInput';
import { AgentCard } from '../../agents';
import { useToast } from '../../../hooks';
import { useProject } from '../../../contexts/ProjectContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { ProjectDetailPage } from '../../projects';
import { STORAGE_KEYS, WELCOME_MESSAGES } from '../constants';

/**
 * NewChatPage - Página para iniciar nova conversa
 */
export const NewChatPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentProject } = useProject();
  const { currentWorkspace } = useWorkspace();
  const { showSuccess, ToastContainer } = useToast();

  // State
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [frequentAgents, setFrequentAgents] = useState<Agent[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  // Initial load
  useEffect(() => {
    const loadInitialAgents = async () => {
      try {
        const response = await agentService.getAvailableAgents();
        if (response.success) {
          setTotalCount(response.metadata?.total ?? null);

          const lastUsedAgentId = localStorage.getItem(STORAGE_KEYS.lastUsedAgentId);
          const lastAgent = lastUsedAgentId
            ? response.data.find((agent: Agent) => agent.id === parseInt(lastUsedAgentId))
            : undefined;
          setSelectedAgent(prev => prev ?? lastAgent ?? response.data[0] ?? null);
        }
      } catch (error) {
        console.error('Error loading agents:', error);
      }
    };
    loadInitialAgents();
  }, []);

  // Load frequent agents
  useEffect(() => {
    agentService.getFrequentAgents(5)
      .then(setFrequentAgents)
      .catch((err) => console.error('Error loading frequent agents:', err));
  }, []);

  // Set random welcome message on mount
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * WELCOME_MESSAGES.length);
    setWelcomeMessage(WELCOME_MESSAGES[randomIndex]);
  }, []);

  // Retorno do catálogo: seleciona e mostra toast (state é limpo para não repetir no refresh)
  useEffect(() => {
    const returned = location.state?.selectedAgent as Agent | undefined;
    if (returned) {
      setSelectedAgent(returned);
      showSuccess(`Agora conversando com ${returned.name}`);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Handle start conversation
  const handleStartConversation = async (files?: File[]) => {
    if (!question.trim() || isLoading || !selectedAgent) return;

    setIsLoading(true);

    try {
      // ✅ CRITICAL: Pass workspace_id for proper token accounting
      const sessionData = await createChatSession(
        selectedAgent.id,
        currentProject?.id || null,
        currentWorkspace?.id
      );
      const sessionId = sessionData.data.id;

      localStorage.setItem(STORAGE_KEYS.pendingChatQuestion, question);
      localStorage.setItem(STORAGE_KEYS.lastUsedAgentId, selectedAgent.id.toString());

      if (files && files.length > 0) {
        sessionStorage.setItem(
          STORAGE_KEYS.pendingChatFiles,
          JSON.stringify(files.map((f) => ({ name: f.name, size: f.size, type: f.type })))
        );
        (window as unknown as { pendingFiles?: File[] }).pendingFiles = files;
      }

      window.dispatchEvent(new CustomEvent('sessionCreated', {
        detail: { projectId: currentProject?.id || null },
      }));

      navigate(`/chat/${sessionId}`);
    } catch (error) {
      console.error('Error starting conversation:', error);
      setIsLoading(false);
    }
  };

  // Seleção via card de agente frequente
  const handleSelectFrequent = (agent: Agent) => {
    setSelectedAgent(agent);
    localStorage.setItem(STORAGE_KEYS.lastUsedAgentId, agent.id.toString());
    showSuccess(`Agora conversando com ${agent.name}`);
  };

  const goToCatalog = () => navigate('/agentes', { state: { returnTo: '/chat' } });

  // Show project page if project is active
  if (currentProject) {
    return <ProjectDetailPage />;
  }

  return (
    <div className="flex flex-col h-screen bg-[#242424]">
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-2xl text-center">
          {/* Title with Icon */}
          <div className="flex items-center justify-center gap-3 mb-2">
            <img
              src="/logo-tais.svg"
              alt="TAIS Logo"
              className="w-10 h-10 flex-shrink-0"
            />
            <h1 className="text-white text-2xl font-semibold">
              {welcomeMessage || 'Olá! Como posso ajudar você hoje?'}
            </h1>
          </div>
          <p className="text-[#888888] text-sm mb-8">
            Comece a digitar ou escolha um agente abaixo
          </p>

          {/* Input */}
          <div className="relative">
            <ChatInput
              value={question}
              onChange={setQuestion}
              onSend={handleStartConversation}
              disabled={isLoading || !selectedAgent}
              placeholder="Digite sua pergunta..."
              selectedAgentName={selectedAgent?.name}
              onAgentClick={goToCatalog}
              showAgentSelector
            />
          </div>

          {/* Seus agentes frequentes */}
          {frequentAgents.length > 0 && (
            <div className="w-full max-w-2xl mx-auto mt-8 text-left">
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-[#888888] text-[11px] font-semibold tracking-widest uppercase">
                  Seus agentes frequentes
                </span>
                <button onClick={goToCatalog} className="text-[#F4CC04] text-sm font-medium hover:text-[#d4b104] transition-colors">
                  {totalCount ? `Ver todos os ${totalCount} →` : 'Ver todos →'}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {frequentAgents.map((agent) => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    isActive={selectedAgent?.id === agent.id}
                    onSelect={handleSelectFrequent}
                    variant="home"
                  />
                ))}
                <button
                  onClick={goToCatalog}
                  className="
                    bg-[#1a1a1a] border border-dashed border-[#444444] rounded-xl p-3.5
                    flex items-center justify-center gap-2 text-[#888888] text-sm font-medium
                    hover:border-[#F4CC04]/50 hover:text-white transition-all min-h-[72px]
                  "
                >
                  <Search size={15} />
                  Buscar agente...
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast container */}
      <ToastContainer />
    </div>
  );
};
